import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { EMPTY, Observable, of, tap, catchError, finalize } from 'rxjs';
import { CdRequestService } from 'src/app/services/cd-request.service';
import { NotificationService } from 'src/app/services/notification.service';
import { toBoolOrUndefined } from 'src/utils';
import { CdRequestWizardBaseComponent } from '../cd-request-wizard-base/cd-request-wizard-base.component';
import { BrokerInformationComponent, BrokerInformationFormData } from './steps/broker-information/broker-information.component';
import { DocOrderFeesComponent, DocOrderFeesFormData } from './steps/doc-order-fees/doc-order-fees.component';
import { EscrowInformationComponent, EscrowInformationFormData } from './steps/escrow-information/escrow-information.component';
import { LoanInformationComponent, LoanInformationFormData } from './steps/loan-information/loan-information.component';
import { PropertyInformationComponent, PropertyInformationFormData } from './steps/property-information/property-information.component';
import { SellerAgentComponent, SellerAgentFormData } from './steps/seller-agent/seller-agent.component';
import { BuyerAgentComponent, BuyerAgentFormData } from './steps/buyer-agent/buyer-agent.component';
import { SettlementAgentComponent, SettlementAgentFormData } from './steps/settlement-agent/settlement-agent.component';
import { TitleInformationComponent, TitleInformationFormData } from './steps/title-information/title-information.component';
import { AdditionalCommentsComponent, AdditionalCommentsFormData } from './steps/additional-comments/additional-comments.component';
import {
  buildContactCopySource,
  CdContactCopySource,
  CdContactCopySourceKey,
  CdContactCopySourceProvider,
  CdContactSnapshot
} from './steps/cd-contact-copy';
import {
  ClosingDisclosureDocRequestFormModel,
  BrokerInformationSection,
  PropertyInformationSection,
  LoanInformationSection,
  FeesSection,
  CdContactInfo,
  TitleInfo,
  CdPropertyType,
  CdLoanPurpose,
  CdAmortizationType
} from 'src/app/cd-request/models/closing-disclosure-doc-request.model';
import { Utils } from 'src/app/core/services/utils';

export interface WholesaleCdWizardFormData {
  brokerInfo: BrokerInformationFormData | null;
  propertyInfo: PropertyInformationFormData | null;
  loanInfo: LoanInformationFormData | null;
  docOrderFees: DocOrderFeesFormData | null;
  sellerAgent: SellerAgentFormData | null;
  buyerAgent: BuyerAgentFormData | null;
  titleInfo: TitleInformationFormData | null;
  escrowInfo: EscrowInformationFormData | null;
  settlementAgent: SettlementAgentFormData | null;
  additionalComments: AdditionalCommentsFormData | null;
}

@Component({
  selector: 'wholesale-cd-wizard',
  templateUrl: './wholesale-cd-wizard.component.html',
  styleUrls: ['./wholesale-cd-wizard.component.scss'],
  standalone: false
})
export class WholesaleCdWizardComponent extends CdRequestWizardBaseComponent implements OnInit, CdContactCopySourceProvider {

  @ViewChild('brokerInfoStep') brokerInfoStep: BrokerInformationComponent;
  @ViewChild('docOrderFeesStep') docOrderFeesStep: DocOrderFeesComponent;
  @ViewChild('escrowInfoStep') escrowInfoStep: EscrowInformationComponent;
  @ViewChild('loanInfoStep') loanInfoStep: LoanInformationComponent;
  @ViewChild('propertyInfoStep') propertyInfoStep: PropertyInformationComponent;
  @ViewChild('sellerAgentStep') sellerAgentStep: SellerAgentComponent;
  @ViewChild('buyerAgentStep') buyerAgentStep: BuyerAgentComponent;
  @ViewChild('settlementAgentStep') settlementAgentStep: SettlementAgentComponent;
  @ViewChild('titleInfoStep') titleInfoStep: TitleInformationComponent;
  @ViewChild('additionalCommentsStep') additionalCommentsStep: AdditionalCommentsComponent;

  steps: MenuItem[] = [];
  wizardTitle: string = 'Wholesale CD Request';

  formModel: ClosingDisclosureDocRequestFormModel | null = null;
  isLoadingData: boolean = false;
  hasLoadError: boolean = false;
  loadErrorMessage: string = '';
  isReadOnly: boolean = false;

  private lastSavedState: string | null = null;

  constructor(
    protected override readonly injector: Injector,
    private readonly _notificationService: NotificationService,
    private readonly _cdRequestService: CdRequestService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.initializeSteps();
    this.loadRequestData();
  }

  protected loadRequestData(): void {
    const loanId = this.applicationContext?.application?.applicationId;
    if (!loanId) {
      this.hasLoadError = true;
      this.loadErrorMessage = 'Unable to identify the loan for this CD request. Refresh the page and try again.';
      this._notificationService.showError(this.loadErrorMessage, 'Load Failed');
      return;
    }

    this.isLoadingData = true;
    this.hasLoadError = false;
    this.loadErrorMessage = '';

    this._cdRequestService.getCdRequest(loanId).pipe(
      catchError(error => {
        if (error?.status === 404) {
          return of(null);
        }
        console.error('Error loading CD request data:', error);
        this.hasLoadError = true;
        this.loadErrorMessage = 'Unable to load the existing CD request. Refresh the page and try again before making changes.';
        this._notificationService.showError(this.loadErrorMessage, 'Load Failed');
        return EMPTY;
      }),
      finalize(() => this.isLoadingData = false)
    ).subscribe({
      next: (response) => {
        if (response) {
          this.formModel = response;
          this.submissionStatus = response.status || null;
          this.submittedDate = response.submittedDate ? new Date(response.submittedDate) : null;
          this.isReadOnly = response.isSubmitted === true
            || response.status?.toLowerCase() === 'submitted'
            || this.submittedDate !== null;
        }
        // Snapshot initial state after a tick so child components populate from @Input changes
        setTimeout(() => {
          this.lastSavedState = JSON.stringify(this.collectAllFormData());
        });
      }
    });
  }

  private initializeSteps(): void {
    this.steps = [
      { label: 'Broker Information' },
      { label: 'Property Information' },
      { label: 'Loan Information' },
      { label: 'Fee Details' },
      { label: 'Seller Real Estate Agent' },
      { label: 'Buyer\'s Real Estate Agent' },
      { label: 'Title Information' },
      { label: 'Escrow Information' },
      { label: 'Settlement Agent' },
      { label: 'Additional Comments' }
    ];
  }

  get transformedSteps(): MenuItem[] {
    return this.steps.map((step, index) => ({
      ...step,
      label: index === this.activeStepIndex ? step.label : ''
    }));
  }

  validateCurrentStep():boolean {
    switch (this.activeStepIndex) {
      case 0:
        return this.validateBrokerInformationStep();
      case 1:
        return this.validatePropertyInformationStep();
      case 2:
        return this.validateLoanInformationStep();
      case 3:
        return this.validateDocOrderFeesStep();
      case 4:
        return this.validateSellerAgentStep();
      case 5:
        return this.validateBuyerAgentStep();
      case 6:
        return this.validateTitleInformationStep();
      case 7:
        return this.validateEscrowInformationStep();
      case 8:
        return this.validateSettlementAgentStep();
      case 9:
        return this.validateAdditionalCommentsStep();
      default:
        return true;
    }
  }

  private validateBrokerInformationStep(): boolean {
    if (this.brokerInfoStep) {
      return this.brokerInfoStep.validateForm();
    }
    return true;
  }

  private validatePropertyInformationStep(): boolean {
    if (this.propertyInfoStep) {
      return this.propertyInfoStep.validateForm();
    }
    return true;
  }

  private validateLoanInformationStep(): boolean {
    if (this.loanInfoStep) {
      return this.loanInfoStep.validateForm();
    }
    return true;
  }

  private validateDocOrderFeesStep(): boolean {
    if (this.docOrderFeesStep) {
      return this.docOrderFeesStep.validateForm();
    }
    return true;
  }

  private validateSellerAgentStep(): boolean {
    if (this.sellerAgentStep) {
      return this.sellerAgentStep.validateForm();
    }
    return true;
  }

  private validateBuyerAgentStep(): boolean {
    if (this.buyerAgentStep) {
      return this.buyerAgentStep.validateForm();
    }
    return true;
  }

  private validateTitleInformationStep(): boolean {
    if (this.titleInfoStep) {
      return this.titleInfoStep.validateForm();
    }
    return true;
  }

  private validateEscrowInformationStep(): boolean {
    if (this.escrowInfoStep) {
      return this.escrowInfoStep.validateForm();
    }
    return true;
  }

  private validateSettlementAgentStep(): boolean {
    if (this.settlementAgentStep) {
      return this.settlementAgentStep.validateForm();
    }
    return true;
  }

  private validateAdditionalCommentsStep(): boolean {
    if (this.additionalCommentsStep) {
      return this.additionalCommentsStep.validateForm();
    }
    return true;
  }

  onStepChange(fromStep: number, toStep: number): void {
  }

  autoSave(): Observable<any> {
    if (this.hasLoadError) {
      return EMPTY;
    }

    if (this.isReadOnly) {
      return of(null);
    }

    const currentData = this.collectAllFormData();
    const currentDataJson = JSON.stringify(currentData);

    if (currentDataJson === this.lastSavedState) {
      return of(null);
    }

    const applicationId = this.applicationContext?.application?.applicationId;
    if (!applicationId) {
      return of(null);
    }

    const mappedData = this.mapFormDataToModel(currentData);

    return this._cdRequestService.saveCdRequestDraft(applicationId, mappedData).pipe(
      tap(() => {
        this.lastSavedState = currentDataJson;
      }),
      catchError(error => {
        console.error('Auto-save failed:', error);
        return of(null);
      })
    );
  }

  canModifyRequest(): boolean {
    return !this.hasLoadError && !this.isReadOnly;
  }

  markAsSubmitted(result: ClosingDisclosureDocRequestFormModel): void {
    this.formModel = result;
    this.submissionStatus = result.status || 'submitted';
    this.submittedDate = result.submittedDate ? new Date(result.submittedDate) : new Date();
    this.isReadOnly = true;
  }

  collectAllFormData(): WholesaleCdWizardFormData {
    return {
      brokerInfo: this.brokerInfoStep?.getFormData() || null,
      propertyInfo: this.propertyInfoStep?.getFormData() || null,
      loanInfo: this.loanInfoStep?.getFormData() || null,
      docOrderFees: this.docOrderFeesStep?.getFormData() || null,
      sellerAgent: this.sellerAgentStep?.getFormData() || null,
      buyerAgent: this.buyerAgentStep?.getFormData() || null,
      titleInfo: this.titleInfoStep?.getFormData() || null,
      escrowInfo: this.escrowInfoStep?.getFormData() || null,
      settlementAgent: this.settlementAgentStep?.getFormData() || null,
      additionalComments: this.additionalCommentsStep?.getFormData() || null
    };
  }

  /** Contact data already entered on the wizard's other parties, offered by the "Copy From" menus. */
  getContactCopySources(excludeKey: CdContactCopySourceKey): CdContactCopySource[] {
    const sellerAgent = this.sellerAgentStep?.getFormData();
    const buyerAgent = this.buyerAgentStep?.getFormData();
    const titleInfo = this.titleInfoStep?.getFormData();
    const escrowInfo = this.escrowInfoStep?.getFormData();
    const settlementAgent = this.settlementAgentStep?.getFormData();

    const sources = [
      sellerAgent && !sellerAgent.noRealEstateAgent
        ? buildContactCopySource('sellerAgent', 'Seller Real Estate Agent', this.toContactSnapshot(sellerAgent, sellerAgent.address))
        : null,
      buyerAgent && !buyerAgent.noRealEstateAgent
        ? buildContactCopySource('buyerAgent', 'Buyer\'s Real Estate Agent', this.toContactSnapshot(buyerAgent, buyerAgent.address))
        : null,
      titleInfo
        ? buildContactCopySource('titleInfo', 'Title Information', this.toContactSnapshot(titleInfo, titleInfo.streetAddress))
        : null,
      escrowInfo
        ? buildContactCopySource('escrowInfo', 'Escrow Information', this.toContactSnapshot(escrowInfo, escrowInfo.address))
        : null,
      settlementAgent
        ? buildContactCopySource('settlementAgent', 'Settlement Agent', this.toContactSnapshot(settlementAgent, settlementAgent.address))
        : null
    ];

    return sources.filter((source): source is CdContactCopySource => source !== null && source.key !== excludeKey);
  }

  private toContactSnapshot(
    formData: SellerAgentFormData | BuyerAgentFormData | TitleInformationFormData | EscrowInformationFormData | SettlementAgentFormData,
    address: string
  ): CdContactSnapshot {
    return {
      companyName: formData.companyName || '',
      companyLicenseId: formData.companyLicenseId || '',
      address: address || '',
      city: formData.city || '',
      // Seller/Buyer state comes from API data with no casing guarantee; the state options are
      // upper-case two-letter codes, so normalize before copying into a destination select.
      state: (formData.state || '').toUpperCase(),
      zip: formData.zip || '',
      contactName: formData.contactName || '',
      contactLicense: formData.contactLicense || '',
      contactEmail: formData.contactEmail || '',
      contactPhone: formData.contactPhone || ''
    };
  }

  protected setNextButtonLabel(): void {
    if (this.activeStepIndex === this.steps.length - 1) {
      this.nextButtonLabel = 'Submit';
    } else {
      this.nextButtonLabel = 'Next';
    }
  }

  protected handleAutoSaveError(error: any): void {
    this._notificationService.showWarning(
      'Auto-save failed. Your changes may not be saved.',
      'Warning'
    );
    console.error('Auto-save error:', error);
  }

  /** Maps raw form data to the backend model. */
  mapFormDataToModel(collectedData: WholesaleCdWizardFormData): Partial<ClosingDisclosureDocRequestFormModel> & { additionalComments?: string } {
    const brokerInfo = collectedData.brokerInfo;
    const propertyInfo = collectedData.propertyInfo;
    const loanInfo = collectedData.loanInfo;
    const docOrderFees = collectedData.docOrderFees;
    const sellerAgent = collectedData.sellerAgent;
    const titleInfo = collectedData.titleInfo;
    const escrowInfo = collectedData.escrowInfo;
    const settlementAgent = collectedData.settlementAgent;

    const brokerInformation: BrokerInformationSection = {
      tpoCompanyName: brokerInfo?.tpoCompanyName || undefined,
      tpoCompanyNmls: brokerInfo?.tpoCompanyNmlsNumber || undefined,
      tpoCompanyPhone: brokerInfo?.tpoCompanyPhone || undefined,
      tpoCompanyFax: brokerInfo?.tpoCompanyFax || undefined,
      processorName: brokerInfo?.processorName || undefined,
      processorEmail: brokerInfo?.processorEmail || undefined,
      loanOfficerName: brokerInfo?.loName || undefined,
      loanOfficerEmail: brokerInfo?.loEmail || undefined
    };

    const propertyInformation: PropertyInformationSection = {
      streetAddress: propertyInfo?.propertyAddress || undefined,
      city: propertyInfo?.city || undefined,
      state: propertyInfo?.state || undefined,
      zip: propertyInfo?.zip || undefined,
      mersMin: propertyInfo?.mersMin || undefined,
      propertyType: propertyInfo?.propertyType as CdPropertyType || undefined,
      mannerTitleHeldAs: propertyInfo?.mannerTitleHeldAs ?? undefined,
      vesting: propertyInfo?.vesting || undefined,
      willUsePoa: toBoolOrUndefined(propertyInfo?.willPoaBeUsed),
      willCloseInTrust: toBoolOrUndefined(propertyInfo?.willLoanCloseInTrust),
      nonBorrowerTitleHolder: toBoolOrUndefined(propertyInfo?.nonBorrowerTitleHolder),
      nonBorrowerTitleHolderName: propertyInfo?.nonBorrowerName || undefined,
      nonBorrowerTitleHolderPhone: propertyInfo?.nonBorrowerPhone || undefined,
      nonBorrowerTitleHolderEmail: propertyInfo?.nonBorrowerEmail || undefined,
      isLlc: toBoolOrUndefined(propertyInfo?.isLlc)
    };

    const loanInformation: LoanInformationSection = {
      loanNumber: loanInfo?.loanNumber || undefined,
      programName: loanInfo?.programName || undefined,
      buydown: loanInfo?.buydown ?? undefined,
      buydownType: loanInfo?.buydownType ?? undefined,
      buydownContributor: loanInfo?.buydownContributor ?? undefined,
      changeBuydown: loanInfo?.changeBuydown ?? undefined,
      loanPurpose: loanInfo?.loanPurpose as CdLoanPurpose || undefined,
      amortizationType: loanInfo?.amortizationType as CdAmortizationType || undefined,
      appraisedValue: loanInfo?.appraisedValue ?? undefined,
      noteRate: loanInfo?.noteRate ?? undefined,
      baseLoanAmount: loanInfo?.baseLoanAmount ?? undefined,
      totalLoanAmount: loanInfo?.totalLoanAmount ?? undefined,
      impoundsWaived: toBoolOrUndefined(loanInfo?.impoundsWaived),
      salesPrice: loanInfo?.salesPrice ?? undefined,
      closeOfEscrowDate: Utils.toUtcStartOfDayIso(loanInfo?.closeOfEscrowDate) || undefined,
      firstPaymentDate: Utils.toUtcStartOfDayIso(loanInfo?.firstPaymentDate) || undefined,
      ltv: loanInfo?.ltv ?? undefined,
      cltv: loanInfo?.cltv ?? undefined
    };

    const fees: FeesSection = {
      applicationFee: docOrderFees?.applicationFee ?? undefined,
      loanOriginationFee: docOrderFees?.loanOriginationFee ?? undefined,
      discountPointFee: docOrderFees?.discountPointFee ?? undefined,
      underwriterFee: docOrderFees?.underwriterFee ?? undefined,
      inHouseProcessingFee: docOrderFees?.inHouseProcessingFee ?? undefined,
      thirdPartyProcessingFee: docOrderFees?.thirdPartyProcessingFee ?? undefined,
      processorFee: docOrderFees?.processorFee ?? undefined,
      appraisalFee: docOrderFees?.appraisalFee ?? undefined,
      creditReportFee: docOrderFees?.creditReportFee ?? undefined,
      surveyFee: docOrderFees?.surveyFee ?? undefined,
      borrowerPaidCompensation: docOrderFees?.borrowerPaidCompensation ?? undefined,
      lenderPaidCompensation: docOrderFees?.lenderPaidCompensation ?? undefined,
      originationCredit: docOrderFees?.originationCredit ?? undefined,
      txAttorneyFee: docOrderFees?.txAttorneyFee ?? undefined
    };

    const sellerRealEstateAgent: CdContactInfo | undefined = !sellerAgent || sellerAgent.noRealEstateAgent ? undefined : {
      companyName: sellerAgent?.companyName || undefined,
      companyLicenseId: sellerAgent?.companyLicenseId || undefined,
      companyAddress: sellerAgent?.address || undefined,
      city: sellerAgent?.city || undefined,
      state: sellerAgent?.state || undefined,
      zip: sellerAgent?.zip || undefined,
      contactFullName: sellerAgent?.contactName || undefined,
      contactLicenseId: sellerAgent?.contactLicense || undefined,
      contactEmailAddress: sellerAgent?.contactEmail || undefined,
      contactPhoneNumber: sellerAgent?.contactPhone || undefined
    };

    const titleInformation: TitleInfo = {
      companyName: titleInfo?.companyName || undefined,
      companyLicenseId: titleInfo?.companyLicenseId || undefined,
      titleCaseNumber: titleInfo?.titleCaseNumber || undefined,
      companyAddress: titleInfo?.streetAddress || undefined,
      city: titleInfo?.city || undefined,
      state: titleInfo?.state || undefined,
      zip: titleInfo?.zip || undefined,
      contactFullName: titleInfo?.contactName || undefined,
      contactLicenseId: titleInfo?.contactLicense || undefined,
      contactEmailAddress: titleInfo?.contactEmail || undefined,
      contactPhoneNumber: titleInfo?.contactPhone || undefined
    };

    const escrowInformation: CdContactInfo = {
      companyName: escrowInfo?.companyName || undefined,
      companyLicenseId: escrowInfo?.companyLicenseId || undefined,
      companyAddress: escrowInfo?.address || undefined,
      city: escrowInfo?.city || undefined,
      state: escrowInfo?.state || undefined,
      zip: escrowInfo?.zip || undefined,
      contactFullName: escrowInfo?.contactName || undefined,
      contactLicenseId: escrowInfo?.contactLicense || undefined,
      contactEmailAddress: escrowInfo?.contactEmail || undefined,
      contactPhoneNumber: escrowInfo?.contactPhone || undefined
    };

    const settlementInformation: CdContactInfo = {
      companyName: settlementAgent?.companyName || undefined,
      companyLicenseId: settlementAgent?.companyLicenseId || undefined,
      companyAddress: settlementAgent?.address || undefined,
      city: settlementAgent?.city || undefined,
      state: settlementAgent?.state || undefined,
      zip: settlementAgent?.zip || undefined,
      contactFullName: settlementAgent?.contactName || undefined,
      contactLicenseId: settlementAgent?.contactLicense || undefined,
      contactEmailAddress: settlementAgent?.contactEmail || undefined,
      contactPhoneNumber: settlementAgent?.contactPhone || undefined
    };

    const buyerAgent = collectedData.buyerAgent;
    const buyerRealEstateAgent: CdContactInfo | undefined = !buyerAgent || buyerAgent.noRealEstateAgent ? undefined : {
      companyName: buyerAgent?.companyName || undefined,
      companyLicenseId: buyerAgent?.companyLicenseId || undefined,
      companyAddress: buyerAgent?.address || undefined,
      city: buyerAgent?.city || undefined,
      state: buyerAgent?.state || undefined,
      zip: buyerAgent?.zip || undefined,
      contactFullName: buyerAgent?.contactName || undefined,
      contactLicenseId: buyerAgent?.contactLicense || undefined,
      contactEmailAddress: buyerAgent?.contactEmail || undefined,
      contactPhoneNumber: buyerAgent?.contactPhone || undefined
    };

    return {
      applicationId: this.applicationContext?.application?.applicationId ?? undefined,
      showCorrespondentOnlyFields: false,
      brokerInformation,
      propertyInformation,
      loanInformation,
      fees,
      sellerRealEstateAgent,
      buyerRealEstateAgent,
      noSellerRealEstateAgentUsed: sellerAgent?.noRealEstateAgent ?? false,
      noRealEstateAgentUsed: buyerAgent?.noRealEstateAgent ?? false,
      titleInformation,
      escrowInformation,
      settlementInformation,
      willSnapDocsBeUsed: brokerInfo?.willSnapDocsBeUsed ?? undefined,
      isHybridClosing: brokerInfo?.isHybridClosing ?? undefined,
      additionalComments: collectedData.additionalComments?.additionalComments || undefined
    };
  }
}
